/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import 'bootstrap/dist/css/bootstrap.min.css';
import { Row } from 'react-bootstrap';
import Container from 'react-bootstrap/Container';
import { useTheme } from 'styled-components';
import { useState, useEffect, useContext } from 'react';
import { useParams } from 'react-router';
import { getCategoriesBySupercatkey, getCategoryCountsBySupercatkey, Category, getSupercat, Supercat, getSetting } from '../data';
import { StatusContext, StatusContextType } from './StatusContext';
import { CategoryCard } from './CategoryCard';

interface CategoryData extends Category {
    fingerCount: number;
    matchCount: number;
}

type PresentMode = 'none' | 'fill' | 'pause';

function SupercatCategoriesPage() {
    const theme: any = useTheme();
    const statusContext = useContext(StatusContext);
    const { supercat } = useParams<{ supercat: string }>();
    const [categories, setCategories] = useState<CategoryData[]>([]);
    const [supercatData, setSupercatData] = useState<Supercat | null>(null);
    const [loading, setLoading] = useState(true);
    const [presentMode, setPresentMode] = useState<PresentMode>('none');
    const [pauseStage, setPauseStage] = useState(0);

    const textStyle = { color: theme.colors.textMuted };

    useEffect(() => {
        async function fetchData() {
            try {
                if (!supercat) {
                    throw new Error('No supercat parameter provided');
                }

                // Fetch categories for this supercat
                const catData = await getCategoriesBySupercatkey(supercat);
                const countData = await getCategoryCountsBySupercatkey(supercat);

                // Merge category data with counts
                const enrichedData: CategoryData[] = catData.map((cat) => ({
                    ...cat,
                    fingerCount: countData.find((c) => c.key === cat.key)?.finger_count || 0,
                    matchCount: countData.find((c) => c.key === cat.key)?.match_count || 0,
                }));
                setCategories(enrichedData);

                // Fetch supercat info
                const scData = await getSupercat();
                const sc = scData.find((s) => s.key === supercat);
                if (sc) {
                    setSupercatData(sc);
                }

                // Get presentation setting
                const presentSetting = await getSetting('/present');
                if (presentSetting === 'fill' || presentSetting === 'pause') {
                    setPresentMode(presentSetting);
                } else {
                    setPresentMode('none');
                }
            } catch (error: any) {
                statusContext.postError(`Error fetching categories: ${error.message}`);
                console.error('Error fetching categories:', error);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [supercat, statusContext]);

    useEffect(() => {
        if (presentMode !== 'pause') {
            setPauseStage(2);
            return;
        }

        setPauseStage(0);
        const handleAdvance = () => {
            setPauseStage((prev) => Math.min(prev + 1, 2));
        };

        window.addEventListener('fbot:present-advance', handleAdvance);
        return () => {
            window.removeEventListener('fbot:present-advance', handleAdvance);
        };
    }, [presentMode]);

    if (loading) {
        return (
            <Container fluid className="pt-3 px-4">
                <p style={textStyle}>Loading categories...</p>
            </Container>
        );
    }

    if (!supercatData) {
        return (
            <Container fluid className="pt-3 px-4">
                <p style={textStyle}>Supercat not found</p>
            </Container>
        );
    }

    const animationsEnabled = presentMode === 'fill' || presentMode === 'pause';

    // Animation timing: stagger cards so all fill in ~1 second
    const cardAnimDuration = 200; // Individual card drop duration
    const totalAnimTime = 1500; // Total time for all cards to appear
    const staggerDelay = categories.length > 1 ? totalAnimTime / (categories.length - 1) : 0;

    // Calculate delays for each category
    const cardsWithDelay: Array<{ category: CategoryData; delay: number; isPlaceholder?: boolean }> = [];

    if (presentMode === 'pause' && pauseStage === 0) {
        return (
            <Container fluid className="pt-3 px-4">
                <Row className="g-3" />
            </Container>
        );
    }

    // Add categories with staggered delays
    categories.forEach((category, index) => {
        cardsWithDelay.push({
            category,
            delay: index * staggerDelay
        });
    });

    return (
        <Container fluid className="pt-3 px-4">
            <div style={{ marginBottom: '1.5rem' }}>
                <h2 style={{ color: theme.colors.text, marginBottom: '0.5rem' }}>
                    {supercatData.title}
                </h2>
                <p style={{ ...textStyle, marginBottom: '0' }}>
                    {supercatData.description}
                </p>
            </div>
            <Row className="g-3">
                {categories.length === 0 ? (
                    <div className="alert alert-info" style={{ width: '100%' }}>
                        No categories found for this supercat.
                    </div>
                ) : (
                    cardsWithDelay.map((item) => (
                        <CategoryCard
                            key={item.category.key}
                            category={item.category}
                            fingerCount={item.category.fingerCount}
                            matchCount={item.category.matchCount}
                            animationDelay={item.delay}
                            animate={animationsEnabled}
                            isPlaceholder={item.isPlaceholder}
                        />
                    ))
                )}
            </Row>
        </Container>
    );
}

export default SupercatCategoriesPage;
